import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Container, InputAdornment, IconButton, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import axiosInstance from '../../../http-common';


function ResetPasswordFormInputComponent({
  show_password,
  handleClickShowPassword,
  handleMouseDownPassword,
  value,
  handleChange,
  label,
  autoComplete,
  name,
  id,
  validation,
}) {
  return <FormControl variant="outlined" fullWidth margin="normal">
    <InputLabel htmlFor="outlined-adornment-password">{label}</InputLabel>
    <OutlinedInput
      required
      label={label}
      name={name}
      id={id}
      autoComplete={autoComplete}
      type={show_password ? 'text' : 'password'}
      endAdornment={
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle password visibility"
            onClick={handleClickShowPassword}
            onMouseDown={handleMouseDownPassword}
            edge="end"
          >
            {show_password ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      }
      value={value}
      onChange={handleChange}
    />
    {value?.length > 0 && validation && !validation.valid && (
      <Box sx={{ mt: 1 }}>
        {Object.keys(validation.rules).map((rule) => {
          return (
            !validation.rules[rule] && (
              <Box
                key={rule}
              >
                <Typography
                  variant="caption" color="error">
                  {validation.messages[rule]}
                </Typography>
              </Box>
            )
          );
        })}
      </Box>
    )}
  </FormControl>;
}


const ResetPasswordPage = () => {

  const navigate = useNavigate();

  const [sending_request, set_sending_request] = useState(false);

  const [open_snackbar_with_message, set_open_snackbar_with_message] = useState({
    open: false,
    message: "",
    severity: "info"
  });

  const { reset_password_token } = useParams(); // Extract the token from the URL
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const validatePassword = (password) => {
    return {
      minLength: password.length >= 8,
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      hasUpperCase: /[A-Z]/.test(password),
    };
  };

  const [passwordValidation, setPasswordValidation] = useState({
    valid: true,
    rules: validatePassword(''),
    messages: {
      minLength: "Password must be at least 8 characters long.",
      hasNumber: "Password must contain at least one number.",
      hasSpecialChar: "Password must contain at least one special character.",
      hasUpperCase: "Password must contain at least one uppercase letter.",
    },
  });
  const [confirmPasswordValidation, setConfirmPasswordValidation] = useState({
    valid: false,
    rules: {
      password_not_matching: password !== confirmPassword
    },
    messages: {
      password_not_matching: "Both passwords should match.",
    },
  });

  const handlePasswordChange = (event) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    const newValidation = validatePassword(newPassword);
    setPasswordValidation({
      valid: Object.values(newValidation).every(v => v),
      rules: newValidation,
      messages: passwordValidation.messages,
    });
  };


  const handleConfirmPasswordChange = (event) => {
    const newConfirmPassword = event.target.value;
    setConfirmPassword(newConfirmPassword);
  };

  useEffect(() => {
    setConfirmPasswordValidation(prev => ({
      valid: password === confirmPassword,
      rules: prev.rules,
      messages: prev.messages,
    }));
  }, [password, confirmPassword]);

  const handleResetPassword = async (event) => {
    try {
      set_sending_request(true);
      event.preventDefault();
      if (password !== confirmPassword) {
        set_open_snackbar_with_message({
          open: true,
          message: "Passwords do not match.",
          severity: "error",
        });
        return;
      }
      // All checks passed, proceed with password reset
      console.log('Password:', password);
      console.log('Confirm Password:', confirmPassword);
      console.log('Reset Password Token:', reset_password_token);
      // Making a request to backend for resetting password
      const res = await axiosInstance.post(`/v1/user/reset-password/${reset_password_token}`, {
        password
      });
      const data = (await res).data;
      set_open_snackbar_with_message({
        open: true,
        message: `${data?.message || "Success"}! Now you can login with the new password.`,
        severity: "success",
      });
      setConfirmPassword("");
      setPassword("");
      navigate("/login");
    } catch (error) {
      console.log(error);
      set_open_snackbar_with_message({
        open: true,
        message: `${error?.response?.data?.message || "Error occured while resetting password."}`,
        severity: "error",
      });
    }
    finally {
      set_sending_request(false);
    }
  };


  const [show_password, set_show_password] = useState(false);
  const [show_confirm_password, set_show_confirm_password] = useState(false);

  const handleClickShowPassword = () => {
    set_show_password(prev => !prev);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleClickShowConfirmPassword = () => {
    set_show_confirm_password(prev => !prev);
  };

  useEffect(() => {
    document.title = "Ripik Optimus - Reset Password";
  }, []);

  return (
    <Container component="main" maxWidth="xs">
      <div className="flex justify-center items-center">
        <img
          className="h-[10rem] mr-4"
          src="/ripikremovedbg.png"
          alt="ripik.ai"
        />
      </div>
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 2 }}>
        <Typography component="h1" variant="h5">
          Reset Your Password
        </Typography>

        <Box component="form" onSubmit={handleResetPassword} sx={{ mt: 1 }}>
          <ResetPasswordFormInputComponent
            value={password}
            handleChange={handlePasswordChange}
            handleClickShowPassword={handleClickShowPassword}
            handleMouseDownPassword={handleMouseDownPassword}
            label={"New Password"}
            autoComplete={"new-password"}
            name={"password"}
            id={"password"}
            show_password={show_password}
            validation={passwordValidation}
          />
          <ResetPasswordFormInputComponent
            value={confirmPassword}
            handleChange={handleConfirmPasswordChange}
            handleClickShowPassword={handleClickShowConfirmPassword}
            handleMouseDownPassword={handleMouseDownPassword}
            label={"Confirm New Password"}
            autoComplete={"new-password"}
            name={"confirmPassword"}
            id={"confirmPassword"}
            show_password={show_confirm_password}
            validation={confirmPasswordValidation}
          />
          <LoadingButton
            disabled={password?.length < 8 || !passwordValidation.valid || !confirmPasswordValidation.valid}
            loading={sending_request}
            variant="contained"
            type="submit"
            color="primary"
            fullWidth
            sx={{ color: "white" }}>
            Reset Password
          </LoadingButton>
        </Box>
      </Box>
    </Container>
  );
};

export default ResetPasswordPage;
